/**
 * D02 — Left navigation drawer.
 *
 * The primary navigation on phones; there is deliberately no bottom tab bar.
 *
 * Adds Notifications and Receipts, which the supplied drawer omitted even
 * though both screens exist and were otherwise unreachable (gap G-08), and
 * points "Settings" at the Profile screen it actually resolves to.
 */

import { StyleSheet, View } from 'react-native';
import type { DrawerContentComponentProps } from '@react-navigation/drawer';
import { DrawerContentScrollView } from '@react-navigation/drawer';
import { getFocusedRouteNameFromRoute } from '@react-navigation/native';

import { colors, radius, sizing, space } from '../../theme';
import { Badge } from '../../components/common/Badge';
import { ListRow } from '../../components/common/ListRow';
import { Logo, TAGLINE, unbrokenPhrases } from '../../components/common/Logo';
import { Text } from '../../components/common/Text';
import { useUnreadCount } from '../../core/api/hooks';
import { homeThen } from './homeUnderneath';
import type { DonorStackParamList } from './types';
import { useLanguage, useT } from '../../core/i18n/LanguageProvider';

interface Entry {
  route: keyof DonorStackParamList;
  label: string;
  icon: string;
}

/**
 * Home first — ordering a tiffin and supporting a meal both start there —
 * then the customer's orders, then reading about it, then help.
 */
const PRIMARY: Entry[] = [
  { route: 'Home', label: 'Home', icon: 'home' },
  { route: 'MyOrders', label: 'My Orders', icon: 'donations' },
  { route: 'Notifications', label: 'Notifications', icon: 'bell' },
  { route: 'Impact', label: 'Impact', icon: 'impact' },
  { route: 'Profile', label: 'Settings', icon: 'settings' },
  { route: 'HowItWorks', label: 'How It Works', icon: 'info' },
  { route: 'Help', label: 'Help & Support', icon: 'help' },
  { route: 'Contact', label: 'Contact Us', icon: 'phone' },
];

export function DrawerContent(props: DrawerContentComponentProps) {
  const t = useT();
  const { isRTL } = useLanguage();
  const { data: unread } = useUnreadCount();
  // The drawer's own state only ever names "Main", so no row was ever marked
  // as the current one. The screen showing is the stack's, one level down.
  const main = props.state.routes[props.state.index];
  // Before the stack has moved, it has no state of its own yet: it is on Home.
  const activeRoute = (main ? getFocusedRouteNameFromRoute(main) : undefined) ?? 'Home';

  const go = (route: keyof DonorStackParamList) => {
    props.navigation.closeDrawer();
    // Straight onto Home, whatever was open before: back from it goes Home.
    props.navigation.dispatch(homeThen(route));
  };

  return (
    <DrawerContentScrollView
      {...props}
      contentContainerStyle={[styles.container, { direction: isRTL ? 'rtl' : 'ltr' }]}
    >
      <View style={styles.brand}>
        <Logo size={40} />
        <View>
          <Text variant="brandName" tone="brand">
            Meal4ALL
          </Text>
          <Text variant="brandTagline" tone="secondary">{unbrokenPhrases(t(TAGLINE))}</Text>
        </View>
      </View>

      <View style={styles.section}>
        {PRIMARY.map((entry) => {
          const active = entry.route === activeRoute;
          return (
            <View
              key={entry.route}
              style={[styles.entry, active && styles.entryActive]}
            >
              <ListRow
                title={t(entry.label)}
                icon={entry.icon}
                onPress={() => go(entry.route)}
                testID={`drawer-${entry.route}`}
                trailing={
                  entry.route === 'Notifications' && unread && unread.count > 0 ? (
                    <Badge label={String(unread.count)} tone="danger" />
                  ) : undefined
                }
              />
            </View>
          );
        })}
      </View>

      <View style={styles.divider} />

      {/* Everything a donor navigates to is above the rule now; what is left
          below it is the one thing that is not a destination in the product. */}
      <View style={styles.section}>
        <ListRow title={t('Privacy & Terms')} icon="privacy" onPress={() => {}} testID="drawer-privacy" />
      </View>
    </DrawerContentScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: space.xl,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    paddingHorizontal: space.lg,
    paddingVertical: space.xl,
  },
  section: {
    paddingHorizontal: space.md,
  },
  entry: {
    borderRadius: radius.md,
    paddingHorizontal: space.sm,
  },
  entryActive: {
    backgroundColor: colors.bg.brandSubtle,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border.subtle,
    marginVertical: space.lg,
    marginHorizontal: space.lg,
  },
});

export const DRAWER_WIDTH_RATIO = sizing.drawerWidthRatio;
